<script setup lang="ts">
import { RouterView } from 'vue-router'
import { watch, computed, ref, onMounted, onUnmounted } from 'vue'
import { useLanguageStore } from './stores/languageStore'
import { useAuthStore } from './stores/auth'
import { useRouter } from 'vue-router'
import { useVideoStore } from './stores/videoStore'
import { VideoProcessingService } from './services/videoProcessingService'
import { useUIStore } from './stores/uiStore'
import apiService from './services/apiService'

const languageStore = useLanguageStore()
const authStore = useAuthStore()
const router = useRouter()
const uiStore = useUIStore()
const adminEmail = (import.meta as any).env?.VITE_ADMIN_EMAIL || 'metinarab@gmail.com'
const isAdmin = computed(() => {
  const email = (authStore.user as any)?.email || ''
  return email && email.toLowerCase() === String(adminEmail).toLowerCase()
})

// For spinner safety checks
let spinnerSafetyInterval: number | null = null;

const isMenuOpen = ref(false)

const switchLanguage = (lang: 'tr' | 'en') => {
  // Sadece dil değişikliğini gerçekleştir, asla sayfayı yenileme
  console.log(`Dil değiştiriliyor: ${lang}, Sayfa: ${router.currentRoute.value.path}`);
  
  // Summaries sayfasındaysak, özel işleme uygula
  if (router.currentRoute.value.path === '/summaries') {
    console.log('🔍 Summaries sayfasında dil değişimi - özel işlem uygulanıyor');
    
    // Önce bir event yayınla, böylece bileşen dinleyebilir
    document.dispatchEvent(new CustomEvent('pre-language-change', { detail: { lang } }));
    
    // Sonra dili değiştir
    languageStore.setLanguage(lang, false);
    
    // Sayfa yenilemesi olmadan hemen sonra bir event daha yayınla
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('post-language-change', { detail: { lang } }));
    }, 100);
  } else {
    // Diğer sayfalarda normal işlemi yap
    languageStore.setLanguage(lang, false);
  }
  
  // Önemli: Tüm komponentlerin yeniden renderlanması language store'da dil 
  // değiştiğinde otomatik olarak gerçekleşiyor
}

// Close menu when clicking outside
const closeMenu = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  const profileButton = document.querySelector('.profile-button')
  const dropdownMenu = document.querySelector('.dropdown-menu')
  
  if (!profileButton?.contains(target) && !dropdownMenu?.contains(target)) {
    isMenuOpen.value = false
  }
}

// Update title when language changes
watch(
  () => languageStore.currentLocale,
  () => {
    document.title = languageStore.t('common.siteTitle')
    console.log('Language changed globally, title updated:', document.title)
  },
  { immediate: true }
)

onMounted(() => {
  document.addEventListener('click', closeMenu)
  
  // URL'den modal parametrelerini kontrol et ve ilgili özeti aç
  const urlParams = new URLSearchParams(window.location.search);
  const modalType = urlParams.get('modal');
  const summaryId = urlParams.get('id');
  
  // Eğer modal=summary&id=XXX formatında bir URL varsa özeti göster
  if (modalType === 'summary' && summaryId) {
    console.log(`📧 Email linkinden özet açılıyor: ${summaryId}`);
    
    // Özet bilgilerini getir ve modalı aç
    // Store üzerinden özet bilgisini getir ve modal'ı aç
    // Bu işlem için Summary store'u veya başka bir servisi kullanmalısınız
    // Örnek bir yaklaşım:
    import('@/stores/summaryStore').then(module => {
      const summaryStore = module.useSummaryStore();
      
      // Önce kullanıcının auth durumunu kontrol et
      if (authStore.user) {
        // Kullanıcı giriş yapmışsa
        summaryStore.openSummary(summaryId);
      } else {
        // Kullanıcı giriş yapmamışsa, önce giriş sayfasına yönlendir
        // ve sonra tekrar özet sayfasına getir
        localStorage.setItem('redirect_after_login', `?modal=summary&id=${summaryId}`);
        
        // Kullanıcıyı login sayfasına yönlendir
        router.push('/');
        
        // Giriş olduktan sonra redirect_after_login parametresi
        // ile yönlendirme authStore içinde yapılacak
      }
    }).catch(err => {
      console.error('Özet store\'u yüklenirken hata:', err);
    });
  }
  
  // Router afterEach hook ile her sayfa değiştiğinde dil kontrolü
  router.afterEach((to, from) => {
    console.log(`Route changed from ${from.path} to ${to.path}, language: ${languageStore.currentLocale}`);
    
    // Sayfa değiştiğinde lokalden kaydedilmiş dil varsa kontrol et
    const savedLocale = localStorage.getItem('userLocale');
    if (savedLocale && (savedLocale === 'tr' || savedLocale === 'en') && savedLocale !== languageStore.currentLocale) {
      console.warn(`Language mismatch - Saved: ${savedLocale}, Current: ${languageStore.currentLocale}. Fixing...`);
      languageStore.setLanguage(savedLocale, false);
    }
  });
  
  // Set up a global safety check for stuck spinners
  const SPINNER_CHECK_INTERVAL = 60 * 1000; // Check every minute
  const MAX_SPINNER_TIME = 5 * 60 * 1000; // 5 minutes max spinner time
  const lastSpinnerStartTimes = new Map<string, number>();
  
  spinnerSafetyInterval = window.setInterval(() => {
    const videoStore = useVideoStore();
    
    // If any spinner is active
    if (videoStore.shouldShowSpinner) {
      console.log('🔍 [Global Safety Check] Checking active spinners...');
      
      const currentTime = Date.now();
      const currentVideoId = videoStore.currentProcessingVideoId;
      
      // Record spinner start time if it's a new spinner
      if (currentVideoId && !lastSpinnerStartTimes.has(currentVideoId)) {
        lastSpinnerStartTimes.set(currentVideoId, currentTime);
        console.log(`🔄 [Global Safety Check] New spinner detected for video ${currentVideoId}`);
      }
      
      // Check all recorded spinners for timeout
      lastSpinnerStartTimes.forEach((startTime, videoId) => {
        const spinnerDuration = currentTime - startTime;
        console.log(`⏱️ [Global Safety Check] Spinner for ${videoId} has been active for ${Math.round(spinnerDuration/1000)}s`);
        
        // If spinner has been active too long
        if (spinnerDuration > MAX_SPINNER_TIME) {
          console.warn(`⚠️ [Global Safety Check] Spinner for ${videoId} has exceeded max time (${MAX_SPINNER_TIME/1000}s), force closing...`);
          
          try {
            // Create instance and force close the spinner
            const currentProcessingLanguage = videoStore.getProcessingLanguage();
            console.log(`🔐 [Global Safety Check] Preserving current processing language: ${currentProcessingLanguage}`);
            
            const videoProcessingService = new VideoProcessingService(
              ref(videoStore.videoData), 
              ref(''), 
              ref(videoStore.processingStatus)
            );
            videoProcessingService.forceCloseSpinners(videoId);
            
            // İşleme dilinin korunduğundan emin olalım
            if (currentProcessingLanguage) {
              console.log(`🔐 [Global Safety Check] Restoring processing language: ${currentProcessingLanguage}`);
              videoStore.setProcessingLanguage(currentProcessingLanguage);
            }
            
            // Remove from tracked spinners
            lastSpinnerStartTimes.delete(videoId);
            
            console.log(`✅ [Global Safety Check] Successfully closed stuck spinner for ${videoId}`);
          } catch (error) {
            console.error(`❌ [Global Safety Check] Error closing spinner:`, error);
          }
        }
      });
      
      // Clean up spinners that are no longer active
      if (!videoStore.shouldShowSpinner) {
        lastSpinnerStartTimes.clear();
        console.log('🧹 [Global Safety Check] Cleared spinner tracking, no active spinners');
      }
    }
  }, SPINNER_CHECK_INTERVAL);

  // Fetch usage on mount if authenticated
  if (authStore.user) {
    apiService.getUsageSummary((authStore.user as any).id).then(r => {
      const d = r?.data;
      if (d) uiStore.setUsage({ used: d.used, limit: d.limit, remaining: d.remaining, resetAt: d.resetAt });
    }).catch(() => {});
  }
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenu)
  
  // Clear the spinner safety interval
  if (spinnerSafetyInterval !== null) {
    clearInterval(spinnerSafetyInterval);
    spinnerSafetyInterval = null;
  }
})
</script>

<template>
  <div class="min-h-screen bg-[#f0f4ff]">
    <!-- Navigation -->
    <nav class="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-100 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo -->
          <router-link to="/" class="flex items-center gap-2">
            <img src="/logo.svg" alt="Veciz AI" class="h-12 w-12" />
            <span class="text-2xl font-black bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-transparent bg-clip-text tracking-tight">Veciz A<span style="font-family: 'Times New Roman', serif; font-weight: 900;">I</span></span>
          </router-link>

          <!-- Right side navigation -->
          <div class="flex items-center gap-4">
            <!-- User Menu -->
            <div class="relative">
              <template v-if="authStore.user">
                <button
                  @click.stop="isMenuOpen = !isMenuOpen"
                  class="profile-button flex items-center gap-2 bg-white p-2 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group border border-gray-100"
                >
                  <div class="text-right hidden sm:block">
                    <div class="text-xs text-gray-500">{{ authStore.user?.email }}</div>
                    <div class="text-sm font-medium">{{ authStore.user?.user_metadata?.name || 'User' }}</div>
                  </div>
                  <div class="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center">
                    <span class="text-lg font-semibold">{{ (authStore.user?.user_metadata?.name || 'U').charAt(0) }}</span>
                  </div>
                </button>
                <div v-if="isMenuOpen" class="dropdown-menu absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 p-2">
                  <router-link to="/summaries" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">My Video Summaries</router-link>
                  <router-link to="/channels" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">Channels</router-link>
                  <router-link to="/settings" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">Settings</router-link>
                  <router-link to="/" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">Home</router-link>
                  <router-link v-if="isAdmin" to="/admin/reports" class="block px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 rounded-lg">Admin Reports</router-link>
                  <router-link v-if="isAdmin" to="/admin/youtube-comments" class="block px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 rounded-lg">YouTube Yorum Yönetimi</router-link>
                  <div class="my-2 border-t border-gray-100"></div>
                  <button @click="authStore.logout()" class="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">Sign out</button>
                </div>
              </template>
              <template v-else>
                <button
                  @click="authStore.login()"
                  class="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-indigo-700"
                  :disabled="authStore.loading"
                >
                  <img src="https://www.google.com/favicon.ico" alt="Google" class="w-4 h-4" />
                  <span class="text-sm font-medium">{{ languageStore.t('common.signInWithGoogle') }}</span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
      
    </nav>

    <!-- Main Content -->
    <router-view :key="$route.fullPath"></router-view>
  </div>

  <footer class="mt-20 py-8 bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <h3 class="text-lg font-semibold mb-4">{{ languageStore.t('home.footer.about.title') }}</h3>
          <p class="text-gray-600">
            {{ languageStore.t('home.footer.about.description') }}
          </p>
        </div>
        <div>
          <h3 class="text-lg font-semibold mb-4">{{ languageStore.t('home.footer.quickLinks.title') }}</h3>
          <ul class="space-y-2">
            <li>
              <a href="/" class="text-accent hover:underline">{{ languageStore.t('home.footer.quickLinks.home') }}</a>
            </li>
          </ul>
        </div>
        <div>
          <h3 class="text-lg font-semibold mb-4">{{ languageStore.t('home.footer.contact.title') }}</h3>
          <p class="text-gray-600">{{ languageStore.t('home.footer.contact.description') }}</p>
        </div>
      </div>
      <div class="mt-8 text-center text-gray-500 text-sm">© {{ new Date().getFullYear() }} Veciz AI</div>
    </div>
  </footer>
</template>

<style scoped>
.logo {
  height: 6em;
  padding: 1.5em;
  will-change: filter;
  transition: filter 300ms;
}
.logo:hover {
  filter: drop-shadow(0 0 2em #646cffaa);
}
.logo.vue:hover {
  filter: drop-shadow(0 0 2em #42b883aa);
}
</style>
